import {
    type FC
} from "react";
import {
    Image,
    View
} from "react-native";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    ItemPartContentProps,
    IItemMediaProps
} from "../../type";
import {
    withItemContext
} from "../../context";
import stylesheet, {
    getItemAvatarSize
} from "../../stylesheet";
import Skeleton from "../../../skeleton";
import Avatar from "../../../avatar";

const ItemMediaBase: FC<ItemPartContentProps<IItemMediaProps>> = ({
    customTheme: customThemeProp,
    variant = "plain",
    icon: IconProp,
    avatarProps,
    children,
    context,
    source,
    style,
    alt,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlHeights,
        avatarSizes,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const avatarSize = getItemAvatarSize({
        controlHeights,
        avatarSizes
    });

    const iconColorKey = context.isDisabled ? context.iconColorKey : "mid";

    const iconColor = context.isDisabled ? context.iconColor : colors.content.icon.mid;

    const renderIcon = (size: number, color: string, key: keyof NCoreUIKit.IconContentColors) => {
        if(!IconProp) {
            return children;
        }

        return <IconProp
            customColor={color}
            size={size}
            color={key}
        />;
    };

    const renderPlain = () => {
        if(context.isLoading) {
            return <Skeleton
                customTheme={customTheme}
                size={context.iconSize}
                variant="icon"
            />;
        }

        return renderIcon(context.iconSize, context.iconColor, context.iconColorKey);
    };

    const renderTile = () => {
        if(context.isLoading) {
            return <Skeleton
                height={controlHeights.small}
                width={controlHeights.small}
                radius={radiuses.actions}
                customTheme={customTheme}
                variant="block"
            />;
        }

        return <View
            style={[
                stylesheet.media,
                stylesheet.mediaTile,
                {
                    backgroundColor: colors.system.state.overlay.fill.default,
                    borderColor: colors.content.border.subtle,
                    borderRadius: radiuses.actions,
                    height: controlHeights.small,
                    width: controlHeights.small,
                    borderWidth: borders.line
                }
            ]}
        >
            {renderIcon(iconSizes.medium, iconColor, iconColorKey)}
        </View>;
    };

    const renderImage = () => {
        if(context.isLoading) {
            return <Skeleton
                height={controlHeights.medium}
                width={controlHeights.medium}
                radius={radiuses.actions}
                customTheme={customTheme}
                variant="block"
            />;
        }

        return <View
            style={[
                stylesheet.mediaTile,
                {
                    borderRadius: radiuses.actions,
                    height: controlHeights.medium,
                    width: controlHeights.medium
                }
            ]}
        >
            <Image
                style={stylesheet.mediaImage}
                accessibilityLabel={alt}
                source={source ?? {}}
                aria-hidden={!alt}
                resizeMode="cover"
                alt={alt}
            />
        </View>;
    };

    const renderAvatar = () => {
        return <Avatar
            {...avatarProps}
            isDisabled={context.isDisabled}
            isLoading={context.isLoading}
            customTheme={customTheme}
            size={avatarSize.key}
        />;
    };

    const renderMedia = () => {
        if(variant === "icon") {
            return renderTile();
        }

        if(variant === "image") {
            return renderImage();
        }

        if(variant === "avatar") {
            return renderAvatar();
        }

        return renderPlain();
    };

    return <View
        {...props}
        style={[
            style,
            stylesheet.media,
            context.isPressable ? stylesheet.inert : undefined,
            context.isMediaTopAligned ? stylesheet.mediaTop : undefined
        ]}
    >
        {renderMedia()}
    </View>;
};
export default withItemContext("media", ItemMediaBase);
